import { useParams, Navigate } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import TrustBar from '../../../components/feature/TrustBar';
import PageHero from '../../../components/feature/PageHero';
import AIAnswerBlock from '../../../components/feature/AIAnswerBlock';
import ProcessSection from '../../../components/feature/ProcessSection';
import WhyChooseUs from '../../../components/feature/WhyChooseUs';
import ServiceAreas from '../../../components/feature/ServiceAreas';
import TestimonialsSection from '../../../components/feature/TestimonialsSection';
import FAQSection from '../../../components/feature/FAQSection';
import FinalCTA from '../../../components/feature/FinalCTA';
import { services, testimonials, process, whyUsPoints, serviceRegions } from '../../../mocks/siteData';
import ServiceDetailServices from './components/ServiceDetailServices';

export default function ServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const service = services.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;

  const aiAnswers = [
    {
      q: `What is ${service.name}?`,
      a: service.fullDesc,
    },
    {
      q: `What does ${service.name} include?`,
      a: `OTT's ${service.name} program covers: ${service.benefits.slice(0, 3).join(', ')}, and more — tailored to your program requirements and vehicle specifications.`,
    },
    {
      q: `Why is ${service.name} important for your brand?`,
      a: `Professional-grade ${service.name} by OTT ensures your vehicles meet the exact presentation standards required for OEM launches, auto shows, fleet programs, and brand activations at the highest level.`,
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      <Navbar />

      {/* 1. Hero */}
      <PageHero
        eyebrow={service.name}
        headline={`${service.name} — Nationwide, Zero Compromise`}
        subheadline={service.shortDesc}
        primaryCTA={{ label: 'Request Event Support', href: '/services' }}
        backgroundImage={service.image}
        badge="Serving All 50 States"
      />

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Services Overview — Service-Specific Benefits */}
      <ServiceDetailServices service={service} />

      {/* 4. AI Answer Block */}
      <AIAnswerBlock
        answers={aiAnswers}
        title={`Everything About ${service.name}`}
        subtitle={`Detailed answers about what OTT's ${service.name} program delivers, who it's built for, and why it matters.`}
      />

      {/* 5. Process */}
      <ProcessSection
        steps={process}
        title="Our Execution Process"
        subtitle={`Every OTT ${service.name} program follows the same precision-driven process — designed for zero-failure execution at any scale.`}
      />

      {/* 6. Why Choose OTT */}
      <WhyChooseUs
        points={whyUsPoints}
        title={`Why OTT for ${service.name}?`}
        subtitle="The standards, infrastructure, and client relationships behind every OTT program."
      />

      {/* 7. Nationwide Coverage */}
      <ServiceAreas
        regions={serviceRegions}
        title={`${service.name} — Nationwide`}
        subtitle="OTT deploys ${service.name.toLowerCase()} crews across all 50 states. No market is out of reach for our national program infrastructure."
        showNational
      />

      {/* 8. Testimonials */}
      <TestimonialsSection
        testimonials={testimonials}
        title="What Our Partners Say"
        subtitle="Brands and agencies who have trusted OTT for events, launches, and fleet programs nationwide."
      />

      {/* 9. FAQ */}
      <FAQSection
        faqs={service.faqs}
        title={`${service.name} — FAQ`}
        subtitle="Common questions about booking, pricing, and what to expect from OTT's program execution."
      />

      {/* 10. Final CTA */}
      <FinalCTA
        headline={`Start Your ${service.name} Program`}
        subheadline={`OTT has the national crew network, operational precision, and zero-compromise quality standard your program requires. Let's talk.`}
      />

      <Footer />
    </div>
  );
}
